import { useState } from "react";
import { AdminBreadcrumbs } from "@/components/admin/AdminBreadcrumbs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, EyeOff, Copy, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function ConfiguracoesPagarme() {
  const [showApiKey, setShowApiKey] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado para área de transferência");
  };

  return (
    <div className="space-y-6">
      {/* AdminBreadcrumbs é automático baseado na rota */}

      <div>
        <h1 className="text-3xl font-bold tracking-tight">Configurações Pagar.me</h1>
        <p className="text-muted-foreground">Gerencie as credenciais e webhooks do Pagar.me</p>
      </div>

      <Tabs defaultValue="production" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="production">Produção</TabsTrigger>
          <TabsTrigger value="sandbox">Homologação</TabsTrigger>
          <TabsTrigger value="webhooks">Logs de Webhook</TabsTrigger>
        </TabsList>

        <TabsContent value="production" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Ambiente de Produção</CardTitle>
                  <CardDescription>Credenciais para transações reais</CardDescription>
                </div>
                <Badge variant="default" className="gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Ativo
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="prod_public_key">Chave Pública</Label>
                <div className="flex gap-2">
                  <Input
                    id="prod_public_key"
                    type={showApiKey ? "text" : "password"}
                    defaultValue="pk_live_xxxxxxxxxxxxxxxx"
                    readOnly
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleCopy("pk_live_xxxxxxxxxxxxxxxx")}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="prod_secret_key">Chave Secreta</Label>
                <div className="flex gap-2">
                  <Input
                    id="prod_secret_key"
                    type={showSecretKey ? "text" : "password"}
                    defaultValue="sk_live_xxxxxxxxxxxxxxxx"
                    readOnly
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setShowSecretKey(!showSecretKey)}
                  >
                    {showSecretKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleCopy("sk_live_xxxxxxxxxxxxxxxx")}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="prod_webhook_url">URL do Webhook</Label>
                <div className="flex gap-2">
                  <Input
                    id="prod_webhook_url"
                    defaultValue="https://seu-dominio.com/api/pagarme-webhook"
                    readOnly
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleCopy("https://seu-dominio.com/api/pagarme-webhook")}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <Button>Atualizar Credenciais</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sandbox" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Ambiente de Homologação</CardTitle>
                  <CardDescription>Credenciais para testes</CardDescription>
                </div>
                <Badge variant="secondary" className="gap-1">
                  <XCircle className="h-3 w-3" />
                  Inativo
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="test_public_key">Chave Pública de Teste</Label>
                <div className="flex gap-2">
                  <Input
                    id="test_public_key"
                    type="text"
                    defaultValue="pk_test_xxxxxxxxxxxxxxxx"
                    readOnly
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleCopy("pk_test_xxxxxxxxxxxxxxxx")}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="test_secret_key">Chave Secreta de Teste</Label>
                <div className="flex gap-2">
                  <Input
                    id="test_secret_key"
                    type="text"
                    defaultValue="sk_test_xxxxxxxxxxxxxxxx"
                    readOnly
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleCopy("sk_test_xxxxxxxxxxxxxxxx")}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <Button>Ativar Ambiente de Teste</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="webhooks" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Logs de Webhook</CardTitle>
              <CardDescription>Últimos eventos recebidos do Pagar.me</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">Implementação de logs em desenvolvimento...</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
